import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { startCase, toLower } from 'lodash'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import Dinero from 'dinero.js'
import { Grid, IconButton, Tooltip, Typography, Skeleton, Alert, AlertTitle } from '@mui/material'
import { makeStyles } from '@mui/styles'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { randomId } from '@mui/x-data-grid-generator'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import { setSnackBar, showFormModal } from 'state/ui'
import { useGetPriceSheetQuery, useUpdatePriceSheetMutation } from 'services/priceSheets'
import { useGetMaterialTypesQuery } from 'services/materialTypes'
import { useDeletePriceEntryMutation } from 'services/priceEntries'
import MaterialAutocomplete from 'components/generic/form/AsyncAutocomplete/MaterialAutocomplete'
import DataGridWithActionButtons from 'components/generic/datagrid/DataGridWithActionButtons'
import Loading from 'components/generic/Loading'
import {
  formatInitialPriceListMaterialData,
  formatSubmitPriceListMaterialData,
} from 'components/containers/commodities/price-lists/utils'
import { dispatchSnackbar, setDecimalPrecision, downloader } from 'utils/helpers'
import FORM_MODALS from 'utils/ui/modals'
import { trackEvent } from 'utils/tracking'
import { CHURN_ZERO_EVENTS as cz, MEASURE_UNIT } from 'utils/constants'
import Input from 'components/generic/form/Input'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import { CloudDownload as CloudDownloadIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material'
import { useGetPriceSheetCsvLazyQuery, usePutPriceSheetCsv2Mutation } from 'services/commodities'
import PriceListUploadErrorsBlade from 'components/containers/commodities/price-lists/components/PriceListUploadErrorsBlade'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'

const useStyles = makeStyles(theme => ({
  root: {
    border: 'none',
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

const PriceListMaterials = ({ selectedPriceList, setMaterialFormIsDirty }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const apiRef = useGridApiRef()
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { dirtyFields },
  } = useForm()
  const isDirty = !!Object.keys(dirtyFields).length

  const { defaultCurrency, settings, defaultYardUm } = useSelector(state => state.settings)
  const { disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}

  const [materialRows, setMaterialRows] = useState([])
  const [saveButtonText, setSaveButtonText] = useState('Save Changes')
  const [defaultPricesDict, setDefaultPricesDict] = useState({})
  const [showCloseModal, setShowCloseModal] = useState(null)

  const { data: priceSheetData, isFetching: isFetchingPriceSheetData } = useGetPriceSheetQuery(
    { priceSheetId: selectedPriceList?.id },
    { skip: !selectedPriceList?.id },
  )
  // prefetch initial material dropdown results (GREEN-8185)
  useGetMaterialTypesQuery({ filter: JSON.stringify({ search: '', priceSheetId: '' }), pageSize: 50 })
  const [updatePriceSheet, { isLoading: isLoadingUpdatingPriceSheet }] = useUpdatePriceSheetMutation()
  const isLoading = isFetchingPriceSheetData || isLoadingUpdatingPriceSheet
  const { decimalPrecision: decimalPrecisionSetting = 2 } = useSelector(
    state => state.settings?.settings?.purchaseSettings?.inboundTickets || {},
  )

  const { organization, selectedYard } = useSelector(state => state.auth)
  const [uploadErrorsList, setUploadErrorsList] = useState([])

  const handleUploadError = error => {
    // Parse data as JSON. This is necessary because the route is set to return raw data
    const data = error?.data ? JSON.parse(error.data) : []
    const errorList = data.errors.slice(null, 10)
    setUploadErrorsList(errorList)

    trackEvent({
      eventName: cz.UPLOAD_PRICE_SHEET_CSV,
    })
  }

  const handleDownloadError = () => {
    const errorMsg = 'CSV download failed.'

    // TODO Should we track upload/download times?
    trackEvent({
      eventName: cz.DOWNLOAD_PRICE_SHEET_CSV,
      errorMsg,
    })
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg,
      }),
    )
  }

  const handleSuccess = isUpload => {
    trackEvent(isUpload ? cz.UPLOAD_PRICE_SHEET_CSV : cz.DOWNLOAD_PRICE_SHEET_CSV)
    setUploadErrorsList([])
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: `CSV file ${isUpload ? 'uploaded' : 'downloaded'} successfully`,
      }),
    )
  }

  const [getPriceSheetCsv, { data: csvData, isLoading: isLoadingGetCsv, isError, error: getCsvError, isSuccess }] =
    useGetPriceSheetCsvLazyQuery()
  const [putPriceSheetCsv, { isLoading: isLoadingPutCsv }] = usePutPriceSheetCsv2Mutation()

  const handleGetPriceSheetCsv = () => getPriceSheetCsv({ priceSheetId: priceSheetData.id })
  const [deletePriceEntry, { isLoading: isLoadingDeletePriceEntry }] = useDeletePriceEntryMutation()

  useEffect(() => {
    if (!csvData) return

    const today = new Date()

    // replace any non-alphanumeric characters with underscores
    const priceListName = priceSheetData.displayName.replace(/[^a-z0-9_-]/gi, '_')
    downloader(
      csvData,
      'text/csv',
      `${organization}-${selectedYard}-${priceListName}-${today.toISOString().split('T')[0]}-price-list.csv`,
    )
  }, [csvData])

  useEffect(() => {
    if (isSuccess) {
      handleSuccess(false)
    }
    if (isError) {
      handleDownloadError(getCsvError)
    }
  }, [isError, isSuccess])

  useEffect(() => {
    if (isDirty) setMaterialFormIsDirty(true)
    else setMaterialFormIsDirty(false)
  }, [isDirty])

  const uploaderBtnRef = useRef()
  const handlePutCsv = () => {
    uploaderBtnRef.current.click()
  }

  const submitCsvFile = async event => {
    const selectedCsv = event.target.files[0]
    if (!selectedCsv) return

    const file = new FormData()
    file.append('data', selectedCsv, 'priceSheetFile.csv')
    await putPriceSheetCsv({ body: file, priceSheetId: priceSheetData.id })
      .unwrap()
      .then(() => {
        handleSuccess(true)
      })
      .catch(err => {
        handleUploadError(err)
      })
  }

  const getMaterials = useCallback(() => Array.from(apiRef.current?.getRowModels().values() || []), [apiRef])

  // set up initial materials
  useEffect(() => {
    if (priceSheetData?.entriesObj) {
      setMaterialRows(formatInitialPriceListMaterialData(priceSheetData?.entriesObj, defaultPricesDict))
    }
  }, [priceSheetData])

  const priceListMargin = useMemo(() => priceSheetData?.margin, [priceSheetData])

  useEffect(() => {
    const resetPayload = {}
    materialRows.forEach(material => {
      resetPayload[material.id] = {
        commercialBuyPrice: material.commercialBuyPrice,
      }
    })
    reset(resetPayload)
  }, [materialRows])

  const onSubmit = async () => {
    const formattedMaterialData = formatSubmitPriceListMaterialData(
      getMaterials(),
      priceSheetData,
      defaultCurrency,
    ).filter(material => !!material.materialCode)
    await updatePriceSheet({
      priceSheetId: priceSheetData?.id,
      priceSheet: {
        displayName: priceSheetData?.displayName,
        id: priceSheetData?.id,
        entriesObj: formattedMaterialData,
        type: priceSheetData?.type,
      },
    })
      .unwrap()
      .then(() => {
        setSaveButtonText('Changes Saved')
        dispatchSnackbar(dispatch, 'success', 'Price list updated successfully.')
        trackEvent(cz.UPDATE_PRICE_LIST)
      })
      .catch(error => {
        const errorMsg = error?.data?.error || 'An error occurred while updating the price list.'
        dispatchSnackbar(dispatch, 'error', errorMsg)
        trackEvent({ eventName: cz.UPDATE_PRICE_LIST, errorMsg })
      })
  }

  const onClickNewMaterial = useCallback(() => {
    const id = randomId()
    apiRef.current?.updateRows([{ id, isNew: true, defaultBuyPrice: 0 }])

    apiRef.current?.scrollToIndexes({
      rowIndex: apiRef.current?.getRowsCount() - 1,
    })

    apiRef.current?.setCellFocus(id, 'material')
  }, [apiRef])

  const onDeleteMaterial = async materialTypeId => {
    const { id: priceSheetId } = selectedPriceList
    await deletePriceEntry({ priceSheetId, materialTypeId })
      .unwrap()
      .then(() => {
        dispatchSnackbar(dispatch, 'success', 'Price list updated successfully.')
        trackEvent(cz.UPDATE_PRICE_LIST)
      })
      .catch(error => {
        const errorMsg = error?.data?.error || 'An error occurred while updating the price list.'
        dispatchSnackbar(dispatch, 'error', errorMsg)
        trackEvent({ eventName: cz.UPDATE_PRICE_LIST, errorMsg })
      })
    setShowCloseModal(null)
  }

  const editActionsLeft = [
    {
      text: 'Add Material',
      variant: 'contained',
      id: 'add-button',
      icon: <AddIcon />,
      onClick: onClickNewMaterial,
      disabled: isLoading,
    },
  ]

  const editActionsRight = [
    {
      text: 'Upload Prices',
      variant: 'text',
      id: 'upload-csv-button',
      icon: <CloudUploadIcon />,
      disabled: isLoading,
      onClick: handlePutCsv,
    },
    {
      text: 'Download Prices',
      variant: 'text',
      id: 'download-csv-button',
      icon: <CloudDownloadIcon />,
      disabled: isLoading,
      onClick: handleGetPriceSheetCsv,
    },
    {
      text: isDirty ? 'Save Changes' : saveButtonText,
      variant: 'contained',
      id: 'save-changes-button',
      onClick: handleSubmit(onSubmit),
      disabled: isLoading || !isDirty,
    },
    {
      text: 'Cancel',
      variant: 'text',
      id: 'cancel-button',
      onClick: () => {
        setMaterialRows(formatInitialPriceListMaterialData(priceSheetData?.entriesObj, defaultPricesDict))
      },
      disabled: isLoading || !isDirty,
    },
  ]

  const handleFormulaClick = row => {
    const priceEntry = priceSheetData.entriesObj.find(({ id }) => id === row.id)
    dispatch(
      showFormModal({
        formModalName: FORM_MODALS.EDIT_FORMULA,
        formModalProps: { priceEntry, priceSheetId: priceSheetData?.id, rowId: row.id },
      }),
    )
  }

  const columns = useMemo(
    () => [
      {
        field: 'material',
        headerName: 'Material',
        flex: 2,
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          const { materialName, materialCode, materialTypeId } = row
          return (
            <MaterialAutocomplete
              variant="outlined"
              size="small"
              defaultValue={{ commonName: materialName, code: materialCode, materialTypeId }}
              preventDuplicateMaterials
              apiRef={apiRef}
              canAutoSelect
              onChange={(value, reason) => {
                const defaultBuyPrice = Dinero({ ...value?.defaultPriceEntry?.buyPrice?.price }).toUnit()
                const defaultPriceUm = value?.defaultPriceEntry?.weightUnits
                const newRow = {
                  ...row,
                  code: value?.code,
                  commodity: `${startCase(toLower(value?.commodityName))}`,
                  materialName: value?.commonName,
                  materialCode: value?.code,
                  materialTypeId: value?.id,
                  defaultBuyPrice,
                  defaultUm: defaultPriceUm,
                }
                if (reason) {
                  newRow.isFormula = false
                  newRow.commercialBuyPrice = defaultBuyPrice
                  setValue(`${row.id}.commercialBuyPrice`, setDecimalPrecision(defaultBuyPrice), { shouldDirty: true })
                  newRow.um = defaultPriceUm
                }
                apiRef.current.updateRows([newRow])
                setDefaultPricesDict(prevState => ({
                  ...prevState,
                  [value?.id]: { defaultBuyPrice, defaultUm: defaultPriceUm },
                }))
              }}
            />
          )
        },
      },
      {
        field: 'commodity',
        headerName: 'Commodity',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'defaultBuyPrice',
        headerName: 'Default Buy Price',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          const { defaultBuyPrice, defaultUm: defaultPriceUm } = row
          return defaultBuyPrice || defaultBuyPrice === 0 ? (
            <Typography variant="body2">
              {`$${setDecimalPrecision(defaultBuyPrice)}/${disableUmDropdowns ? defaultYardUm : defaultPriceUm || defaultYardUm}`}
            </Typography>
          ) : (
            <Skeleton animation="wave" width="75%" height="1rem">
              <Typography variant="body2" />
            </Skeleton>
          )
        },
      },
      {
        field: 'commercialBuyPrice',
        headerName: `${priceSheetData?.displayName || ''} Buy Price`,
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          const { id, commercialBuyPrice = 0, isFormula, um } = row
          return isFormula ? (
            <Typography variant="body2">
              {`$${setDecimalPrecision(commercialBuyPrice)}/${disableUmDropdowns ? defaultYardUm : um || defaultYardUm}`}
            </Typography>
          ) : (
            <Input
              control={control}
              name={`${id}.commercialBuyPrice`}
              sx={{ width: '100%' }}
              defaultValue={commercialBuyPrice}
              fractionDigits={decimalPrecisionSetting}
              InputProps={{
                inputComponent: InputFormat,
                sx: { height: '40px', fontSize: '13px' },
                inputProps: {
                  inputType: 'price',
                  decimalScale: decimalPrecisionSetting,
                  suffix: `/${disableUmDropdowns ? defaultYardUm : um || defaultYardUm}`,
                  onChange: event => {
                    const newValue = event?.target?.value
                    if (newValue?.toString() === '.') return
                    apiRef.current.updateRows([{ ...row, commercialBuyPrice: newValue }])
                  },
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
      },
      {
        field: 'priceType',
        headerName: 'Formula',
        renderCell: ({ row }) => {
          const { isFormula, isNew } = row
          return (
            <Tooltip
              title={
                isNew
                  ? 'To add a formula to a new price entry, please save the entry first, then return to Edit Mode to add the formula'
                  : ''
              }
            >
              <div>
                <IconButton
                  color={isFormula ? 'primary' : 'default'}
                  size="small"
                  onClick={() => handleFormulaClick(row)}
                  disabled={isNew}
                >
                  {isFormula ? <EditIcon fontSize="small" /> : <AddCircleIcon fontSize="small" />}
                </IconButton>
              </div>
            </Tooltip>
          )
        },
        headerAlign: 'center',
        align: 'center',
        sortable: false,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        renderCell: ({ row }) => (
          <IconButton
            color="error"
            size="small"
            sx={{
              fill: 'red',
            }}
            aria-label="delete"
            disabled={isLoading || isDirty}
            onClick={event => {
              event.stopPropagation()
              if (row.isNew) {
                apiRef.current?.updateRows([{ id: row.id, _action: 'delete' }])
              } else {
                setShowCloseModal(row.materialTypeId)
              }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
        headerAlign: 'center',
        align: 'center',
        sortable: false,
      },
    ],
    [apiRef, priceSheetData, disableUmDropdowns, defaultYardUm, isLoading, isDirty],
  )

  if (isLoadingGetCsv || isLoadingPutCsv) return <Loading />

  return (
    <>
      {isLoadingUpdatingPriceSheet || (isLoadingDeletePriceEntry && <Loading />)}
      <AlertModal
        open={!!showCloseModal}
        modalType="close"
        title="Delete This Material"
        question="Are you sure you want to delete this material from this price list?"
        description="This price list price will no longer apply to newly created inbound tickets with this material."
        confirmLabel="DELETE THIS MATERIAL"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowCloseModal(false)
        }}
        onSubmit={() => {
          onDeleteMaterial(showCloseModal)
        }}
      />
      <input
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        ref={uploaderBtnRef}
        onChange={e => {
          submitCsvFile(e)
        }}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid p={3} flexDirection="column">
          {priceListMargin || priceListMargin === 0 ? (
            <Alert
              variant="filled"
              severity="info"
              sx={{
                bgcolor: 'rgba(33, 150, 243, 0.1)',
                '.MuiAlert-icon': { color: 'rgba(33, 150, 243, 1)' },
                '.MuiAlert-message': { color: 'text.primary' },
              }}
            >
              <AlertTitle>Margin-Based Pricing Activated</AlertTitle>
              {`This price list is currently set up with a margin-based pricing structure. A ${priceListMargin}% margin has been
              applied to all materials. To adjust the margin or for any further customizations, please contact our support team
              and we'll be happy to assist you.`}
            </Alert>
          ) : (
            <>
              <DataGridWithActionButtons
                className={classes.root}
                leftActionButtons={editActionsLeft}
                rightActionButtons={editActionsRight}
                height="auto"
                pagination={false}
                rows={materialRows}
                loading={isLoading}
                columns={columns}
                disableColumnMenu
                onCellClick={(_, event) => event.stopPropagation()}
                hideFooter
                apiRef={apiRef}
              />
              <PriceListUploadErrorsBlade
                open={uploadErrorsList.length > 0}
                setOpen={() => {}}
                onRetryUpload={handlePutCsv}
                onClose={() => setUploadErrorsList([])}
                errorsList={uploadErrorsList}
              />
            </>
          )}
        </Grid>
      </form>
    </>
  )
}

PriceListMaterials.propTypes = {
  selectedPriceList: PropTypes.object.isRequired,
  setMaterialFormIsDirty: PropTypes.func,
}

PriceListMaterials.defaultProps = {
  setMaterialFormIsDirty: () => {},
}

export default PriceListMaterials
